import { useState } from 'react';

interface ResultadoBusca {
  ok: boolean;
  codigo?: string;
  erro?: string;
}

interface ModalDigitarCodigoProps {
  codigos: string[];
  aoLiberar: (codigoUuid: string) => void;
  aoFechar: () => void;
  // Quando existe, a busca é feita no banco (portaria convidada não baixa a lista).
  aoBuscar?: (texto: string) => Promise<ResultadoBusca>;
}

// Normaliza o que a pessoa digitou para só hex minúsculo.
function normalizar(v: string): string {
  return v.replace(/[^0-9a-fA-F]/g, '').toLowerCase();
}

export default function ModalDigitarCodigo({
  codigos,
  aoLiberar,
  aoFechar,
  aoBuscar,
}: ModalDigitarCodigoProps) {
  const [valor, setValor] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [buscando, setBuscando] = useState(false);

  async function enviar() {
    const hex = normalizar(valor);
    if (hex.length < 8) {
      setErro('Digite pelo menos os 8 primeiros caracteres do código.');
      return;
    }

    // Portaria convidada: procura no banco.
    if (aoBuscar) {
      setBuscando(true);
      setErro(null);
      try {
        const r = await aoBuscar(hex);
        if (r.ok && r.codigo) {
          aoLiberar(r.codigo);
          return;
        }
        setErro(r.erro || 'Ingresso não encontrado. Confira o código.');
      } catch {
        setErro('Não conseguimos buscar agora. Tente de novo.');
      } finally {
        setBuscando(false);
      }
      return;
    }

    // Direção: procura na lista já carregada.
    const achados = codigos.filter((c) => c.replace(/-/g, '').startsWith(hex));
    if (achados.length === 0) {
      setErro('Ingresso não encontrado. Confira o código.');
      return;
    }
    if (achados.length > 1) {
      setErro('Código ambíguo. Digite o código completo.');
      return;
    }

    aoLiberar(achados[0]);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 sm:items-center sm:p-6">
      <div className="animate-modal-pop w-full rounded-t-lg bg-white sm:max-w-md sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="text-lg font-medium text-foreground-950">Digitar código</h3>
          <button
            type="button"
            onClick={aoFechar}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="px-5 py-5">
          <label
            htmlFor="portaria-codigo"
            className="block text-sm font-medium text-foreground-950"
          >
            Código do ingresso
          </label>
          <p className="mt-1 text-sm text-secondary-500">
            Digite os 8 primeiros caracteres (ou o código inteiro) do ingresso.
          </p>
          <input
            id="portaria-codigo"
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            value={valor}
            onChange={(e) => {
              setValor(e.target.value);
              setErro(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void enviar();
            }}
            placeholder="A1B2C3D4"
            className="mt-3 w-full rounded-md border border-secondary-300 bg-white px-4 py-3 font-mono text-lg uppercase tracking-wider text-foreground-950 placeholder:text-secondary-300 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
          />
          {erro && (
            <p className="mt-3 text-sm text-danger-600" role="alert">
              {erro}
            </p>
          )}
        </div>

        <div className="flex gap-3 border-t border-secondary-100 px-5 py-4">
          <button
            type="button"
            onClick={aoFechar}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void enviar()}
            disabled={buscando}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
      </div>
    </div>
  );
}